import React from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Brain } from "lucide-react";

// Sample AI-driven insights data
const industryTrends = [
  { year: 2024, automation: 10, AI_adoption: 5 },
  { year: 2025, automation: 18, AI_adoption: 12 },
  { year: 2026, automation: 25, AI_adoption: 20 },
  { year: 2027, automation: 35, AI_adoption: 30 },
  { year: 2030, automation: 55, AI_adoption: 50 },
];

const futureProjections = [
  { year: 2024, marketValue: 2.5 },
  { year: 2026, marketValue: 3.2 },
  { year: 2028, marketValue: 4.5 },
  { year: 2030, marketValue: 6.8 },
];

const InsightsAi = () => {
  return (
    <div className="space-y-6 mb-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Brain className="h-6 w-6 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900">AI-Driven Financial Analysis</h3>
        </div>
        <p className="text-gray-600">
          Artificial Intelligence is transforming financial analysis by leveraging vast datasets, predictive modeling, and automation to provide deeper insights into market trends, business performance, and future projections.
        </p>
        <p className="mt-2 text-gray-600">
          This report explores how AI is shaping the financial landscape, particularly within the food truck industry. Key areas include:
        </p>
        <ul className="mt-2 space-y-2 text-gray-600">
          <li><strong>Industry Trends:</strong> The rapid adoption of AI and automation is revolutionizing food truck operations, from predictive analytics to smart fleet management.</li>
          <li><strong>Market Growth Projections:</strong> AI-driven innovations are expected to increase revenue potential, with market value projections growing significantly by 2030.</li>
          <li><strong>AI-Powered Transformations:</strong> Emerging AI applications, such as automated ordering systems, robotic kitchens, and personalized marketing, will redefine business models.</li>
          <li><strong>Actionable Insights:</strong> Practical recommendations for food truck owners to integrate AI-driven strategies and stay ahead in a competitive landscape.</li>
        </ul>
        <p className="mt-2 text-gray-600">
          By analyzing financial trends, automation levels, and AI adoption rates, this report provides a comprehensive overview of how AI is shaping the industry's future.
        </p>
      </div>


      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">AI Trends in the Food Truck Industry</h2>
        <p>AI adoption in the food truck industry is expected to grow exponentially, especially in automation, predictive analytics, and personalized marketing.</p>
        <div className="overflow-x-auto">
          <div className="w-[600px] sm:w-full">
            <ResponsiveContainer width="100%" height={300} className="mt-4">
              <LineChart data={industryTrends}>
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="automation" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="AI_adoption" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">Market Growth Projections (2024-2030)</h2>
        <p>AI-driven innovations will expand the food truck market, increasing revenue potential significantly.</p>
        <div className="overflow-x-auto">
          <div className="w-[600px] sm:w-full">
            <ResponsiveContainer width="100%" height={300} className="mt-4">
              <BarChart data={futureProjections}>
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <CartesianGrid strokeDasharray="3 3" />
                <Bar dataKey="marketValue" fill="#4F46E5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">How AI Will Transform Food Trucks in 5-10 Years</h2>
        <ul className="mt-4 space-y-2 text-gray-600">
          <li><strong>AI-Driven Menus:</strong> Predictive analytics will optimize food offerings based on weather, events, and local trends.</li>
          <li><strong>Automated Ordering & Payments:</strong> AI-integrated kiosks and voice assistants will streamline customer experiences.</li>
          <li><strong>Robotic Kitchens:</strong> Semi-autonomous cooking will reduce labor costs and improve efficiency.</li>
          <li><strong>Smart Fleet Management:</strong> AI will determine the best locations and times for food truck placement.</li>
          <li><strong>AI-Powered Marketing:</strong> AI will generate hyper-personalized marketing campaigns, increasing customer engagement.</li>
        </ul>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">Recommendations for Food Truck Owners</h2>
        <ul className="mt-4 space-y-2 text-gray-600">
          <li>Invest in <strong>AI-driven order prediction tools</strong> to minimize waste and maximize profits.</li>
          <li>Adopt <strong>mobile payment and digital ordering systems</strong> to enhance convenience.</li>
          <li>Utilize AI-powered <strong>location analytics</strong> to determine high-traffic areas.</li>
          <li>Experiment with <strong>automated kitchen assistants</strong> to improve efficiency.</li>
          <li>Leverage AI-generated marketing strategies to boost visibility and attract new customers.</li>
        </ul>
      </div>
    </div>
  );
};

export default InsightsAi;
